export type WhatsNewAudience =
  | 'admin'
  | 'doctor'
  | 'fisioterapeuta'
  | 'interno'
  | 'patient'
  | 'superAdmin';

export type WhatsNewIcon =
  | 'evolution'
  | 'timeline'
  | 'exam'
  | 'vitals'
  | 'assessment'
  | 'design';

export interface WhatsNewMedia {
  src: string;
  alt: string;
  caption?: string;
}

export interface WhatsNewStep {
  id: string;
  title: string;
  summary: string;
  description: string;
  highlights: string[];
  icon: WhatsNewIcon;
  media?: WhatsNewMedia;
}

export interface WhatsNewRelease {
  id: string;
  versionLabel: string;
  publishedAt: string;
  title: string;
  introduction: string;
  audience: WhatsNewAudience[];
  steps: WhatsNewStep[];
}

export const WHATS_NEW_RELEASES: WhatsNewRelease[] = [
  {
    id: '2026-11-01-concise-assessment-reports',
    versionLabel: 'Novembro de 2026',
    publishedAt: '01/11/2026',
    title: 'Relatórios mais claros e compactos',
    introduction: 'Destaque os resultados e registre sua análise com espaço para o que importa em cada documento.',
    audience: ['admin', 'doctor', 'fisioterapeuta', 'interno', 'superAdmin'],
    steps: [{
      id: 'concise-professional-reports',
      title: 'Resultados objetivos e texto do profissional',
      summary: 'Cabeçalho compacto, resultados resumidos e observações opcionais.',
      description: 'Finalidade mostra apenas a opção escolhida. Os resultados permanecem em tabelas e gráficos; síntese, plano e observações ficam disponíveis para seu registro, sem frases genéricas ou classificação de validade no relatório.',
      highlights: ['Preserva os textos já escritos pelo profissional.', 'Mantém diagnóstico CBDF/CIF e suas evidências no modo técnico.', 'Referências detalhadas permanecem nos laudos individuais dos testes.'],
      icon: 'assessment',
    }],
  },
  {
    id: '2026-10-31-respiratory-monitoring',
    versionLabel: 'Outubro de 2026',
    publishedAt: '31/10/2026',
    title: 'Acompanhamento respiratório e avaliações neuromusculares',
    introduction: 'Registre o peak flow em casa e compare medidas respiratórias com o contexto de cada avaliação.',
    audience: ['admin', 'doctor', 'fisioterapeuta', 'interno', 'superAdmin'],
    steps: [
      {
        id: 'peak-flow-diary', title: 'Um link para acompanhar o peak flow',
        summary: 'O paciente usa o mesmo link para várias aferições durante o período de acompanhamento.',
        description: 'Acesse o diário em Nova avaliação, na seção Fluxo expiratório. Ele reúne medidas de manhã, à noite e extras, sintomas e contexto do broncodilatador, com gráficos, variabilidade e exportação em PDF.',
        highlights: ['Acompanhamento inicial de 14 dias, com duração configurável.', 'Busca por nome, CPF ou prontuário com lista de pacientes sempre visível no computador.', 'Variabilidade calculada com pares de rotina comparáveis.', 'Link individual revogável e identificação dos registros do paciente ou cuidador.'],
        icon: 'timeline',
      },
      {
        id: 'neuromuscular-respiratory-assessments', title: 'Posição e assistência documentadas',
        summary: 'PImax por posição, pico de fluxo de tosse, capacidade vital postural e SNIP.',
        description: 'A PImax conserva tentativas e curvas separadas por posição. As novas avaliações registram a técnica e apresentam comparações históricas compatíveis na pré-visualização e no PDF.',
        highlights: ['Tosse espontânea e assistida apresentadas separadamente.', 'Pico de tosse com percentis pediátricos, comparação com médias adultas e faixas específicas para DNM.', 'Contexto clínico separado da qualidade da medida, com barras também no PDF.', 'Exportação com ou sem histórico, preservando a interpretação profissional.'],
        icon: 'assessment',
      },
    ],
  },
  {
    id: '2026-10-31-integrated-physiotherapy-assessment',
    versionLabel: 'Outubro de 2026',
    publishedAt: '31/10/2026',
    title: 'Avaliação integrada, acompanhamento longitudinal e CBDF',
    introduction: 'Reúna a história, o exame, os testes e o raciocínio clínico em uma avaliação que acompanha o paciente.',
    audience: ['admin', 'doctor', 'fisioterapeuta', 'interno', 'superAdmin'],
    steps: [
      {
        id: 'anamnesis-integrated-assessment', title: 'Continue a avaliação a partir da anamnese',
        summary: 'A anamnese permanece no prontuário e inicia uma avaliação em rascunho.',
        description: 'Abra a avaliação pela timeline ou pelo contexto clínico. Organize história, exame, testes, metas e plano nos momentos inicial, de reavaliação e final.',
        highlights: ['Salve e retome o preenchimento por etapas.', 'Reutilize anamnese, testes concluídos e exames complementares.', 'Escolha sugestões de instrumentos conforme o perfil e a pergunta clínica.'],
        icon: 'assessment',
      },
      {
        id: 'longitudinal-cbdf-assessment', title: 'Escolha o documento para cada finalidade',
        summary: 'Avaliação inicial/final, relatório longitudinal ou achados com diagnóstico fisioterapêutico.',
        description: 'Compare várias datas com tabelas e gráficos opcionais, preservando posição, lado e condições dos testes. O modo técnico mantém CBDF e CIF com confirmação, autoria e evidências do fisioterapeuta.',
        highlights: ['Inclua medidas pontuais mesmo sem avaliação anterior.', 'Acompanhe aumento, redução e manutenção das medidas.', 'Mantenha o diagnóstico CBDF disponível nos diferentes formatos técnicos.'],
        icon: 'timeline',
      },
    ],
  },
  {
    id: '2026-09-16-flexible-patient-search',
    versionLabel: 'Setembro de 2026',
    publishedAt: '16/09/2026',
    title: 'Busca de pacientes mais rápida e flexível',
    introduction:
      'Agora ficou mais fácil encontrar o paciente certo mesmo quando o nome é digitado sem acento ou com uma pequena diferença de escrita.',
    audience: ['admin', 'doctor', 'fisioterapeuta', 'interno', 'superAdmin'],
    steps: [
      {
        id: 'accent-and-similarity-patient-search',
        title: 'Encontre pacientes mesmo com variações no nome',
        summary: 'A busca entende acentos, nomes compostos e pequenos erros de digitação.',
        description:
          'Os campos de busca de pacientes agora priorizam correspondências exatas e também apresentam nomes semelhantes, mantendo documentos e prontuários como identificadores seguros.',
        highlights: [
          '“jose” encontra pacientes cadastrados como “José”.',
          'Pequenas diferenças como “matos” e “Mattos” também são reconhecidas.',
          'Resultados exatos aparecem antes das sugestões semelhantes.',
          'Disponível na agenda, evoluções, avaliações, cadastro, vinculação e financeiro.',
        ],
        icon: 'design',
      },
    ],
  },
  {
    id: '2026-08-30-flexible-batch-scheduling',
    versionLabel: 'Agosto de 2026',
    publishedAt: '30/08/2026',
    title: 'Agendamentos em lote mais rápidos e flexíveis',
    introduction:
      'A agenda agora permite criar um atendimento único, uma série personalizada para o mesmo paciente ou um grupo de pacientes no mesmo horário.',
    audience: ['admin', 'doctor', 'fisioterapeuta', 'interno', 'superAdmin'],
    steps: [
      {
        id: 'appointment-formats',
        title: 'Escolha o formato antes de agendar',
        summary: 'Use o mesmo formulário para criar um atendimento único, uma série ou um grupo.',
        description:
          'O novo seletor de formato organiza o fluxo logo no início. O formato Único continua indicado para um paciente em uma data e horário; Série e Grupo liberam os novos recursos em lote.',
        highlights: [
          'Único: um paciente em uma data e horário.',
          'Série: o mesmo paciente em várias datas e horários.',
          'Grupo: vários pacientes com o mesmo profissional, data e horário.',
          'A busca pode continuar limitada aos pacientes ativos da clínica.',
        ],
        icon: 'timeline',
        media: {
          src: '/whats-new/2026-08-30/agendamento-unico.jpg',
          alt: 'Tela real de novo agendamento com as opções Único, Série e Grupo.',
          caption: 'Novo seletor de formato no formulário de agendamento.',
        },
      },
      {
        id: 'weekly-appointment-series',
        title: 'Séries com uma grade semanal',
        summary: 'Defina dias e horários diferentes e repita a combinação pelas semanas desejadas.',
        description:
          'Na Grade semanal, cada dia escolhido pode ter seu próprio horário. Assim, é possível agendar, por exemplo, terça-feira às 8h e quinta-feira às 10h30 dentro da mesma série.',
        highlights: [
          'Escolha a semana inicial e a quantidade de semanas da repetição.',
          'Adicione mais de um dia e horário à mesma semana.',
          'Cada faixa informa início e fim, com validação de horários sobrepostos.',
          'O resumo mostra previamente a quantidade de sessões e o período da série.',
        ],
        icon: 'timeline',
        media: {
          src: '/whats-new/2026-08-30/serie-grade-semanal.jpg',
          alt: 'Tela real da série de agendamentos no modo Grade semanal.',
          caption: 'Grade semanal com repetição por semanas e horários independentes.',
        },
      },
      {
        id: 'specific-date-appointment-series',
        title: 'Séries com datas específicas',
        summary: 'Monte livremente uma lista de datas e horários, inclusive com mais de uma sessão no mesmo dia.',
        description:
          'O modo Datas específicas atende rotinas irregulares: basta adicionar cada sessão desejada, sem depender de um intervalo fixo entre as ocorrências.',
        highlights: [
          'Cada item possui data, início e fim próprios.',
          'É possível combinar semanas diferentes e sessões isoladas na mesma série.',
          'Mais de uma sessão no mesmo dia é aceita quando os horários não se sobrepõem.',
          'A série é ordenada cronologicamente e pode conter até 200 ocorrências.',
        ],
        icon: 'timeline',
        media: {
          src: '/whats-new/2026-08-30/serie-datas-especificas.jpg',
          alt: 'Tela real da série de agendamentos no modo Datas específicas.',
          caption: 'Lista livre de datas e horários para rotinas sem repetição fixa.',
        },
      },
      {
        id: 'group-appointments-and-batch-safety',
        title: 'Atendimentos em grupo com criação segura',
        summary: 'Selecione vários pacientes e crie todos os agendamentos para o mesmo horário em uma única ação.',
        description:
          'No formato Grupo, cada paciente recebe uma ocorrência própria no mesmo horário e com o mesmo profissional. Séries e grupos são salvos como uma única operação para evitar lotes incompletos.',
        highlights: [
          'Grupos aceitam de 2 a 30 pacientes, sem repetir o mesmo paciente no lote.',
          'As ocorrências são criadas como agendadas em uma única operação: se uma falhar, nenhuma é salva.',
          'A identificação do lote também protege contra duplicações acidentais em uma nova tentativa.',
          'A criação não reserva nem consome sessões de pacote e não calcula repasse.',
          'O indicador de sessões realizadas deixa de contar ocorrências apenas agendadas; conclusão e exclusão continuam sendo feitas por ocorrência.',
        ],
        icon: 'assessment',
        media: {
          src: '/whats-new/2026-08-30/agendamento-em-grupo.jpg',
          alt: 'Tela real do formato Grupo para adicionar vários pacientes ao mesmo horário.',
          caption: 'Formato Grupo com aviso sobre pacote e repasse no momento da criação.',
        },
      },
    ],
  },
  {
    id: '2026-08-19-respiratory-pressure-results',
    versionLabel: 'Agosto de 2026',
    publishedAt: '19/08/2026',
    title: 'Pressões respiratórias mais fáceis de interpretar',
    introduction:
      'Os resultados de PImax, PEmax e S-Index agora mostram diretamente a posição em relação ao limite inferior esperado e descrevem a repetibilidade pela variação observada.',
    audience: ['admin', 'doctor', 'fisioterapeuta', 'interno', 'superAdmin'],
    steps: [
      {
        id: 'respiratory-lln-and-repeatability',
        title: 'Limite esperado e repetibilidade em linguagem direta',
        summary: 'Veja rapidamente se o resultado está abaixo do limite esperado ou dentro da faixa esperada e se as manobras foram reprodutíveis.',
        description:
          'As telas e os laudos de força muscular respiratória deixaram de usar siglas e textos genéricos e passaram a informar o limite inferior esperado calculado pela referência adotada.',
        highlights: [
          'Classificação direta como “Abaixo do limite esperado” ou “Dentro da faixa esperada”.',
          'Avaliações anteriores aproveitam idade e sexo já preservados no exame para recompor o limite esperado.',
          'Variação de 0% entre as melhores manobras é apresentada como reprodutível.',
          'O percentual do previsto continua visível como medida complementar, sem virar uma faixa de gravidade.',
        ],
        icon: 'assessment',
      },
    ],
  },
  {
    id: '2026-08-18-assessment-results',
    versionLabel: 'Agosto de 2026',
    publishedAt: '18/08/2026',
    title: 'Avaliações e relatórios mais fáceis de encontrar',
    introduction:
      'A consulta do histórico ficou mais completa, com busca em toda a base, paginação flexível e referências clínicas rastreáveis nos relatórios.',
    audience: ['admin', 'doctor', 'fisioterapeuta', 'interno', 'superAdmin'],
    steps: [
      {
        id: 'complete-assessment-search',
        title: 'Busca em todo o histórico',
        summary: 'Encontre avaliações mesmo quando elas estiverem em outra página da lista.',
        description:
          'A busca das avaliações agora é feita diretamente na base de dados antes da paginação, considerando todo o histórico disponível para o tenant.',
        highlights: [
          'Busca por paciente, profissional, teste, pontuação, grau ou classificação.',
          'Resultados encontrados independentemente da página em que estavam.',
          'Filtros de situação e profissional continuam sendo respeitados.',
        ],
        icon: 'assessment',
      },
      {
        id: 'assessment-pagination',
        title: 'Mais controle sobre a lista',
        summary: 'Navegue entre páginas e escolha quantos resultados deseja visualizar.',
        description:
          'A lista de avaliações passou a oferecer leitura das avaliações anteriores sem carregar todo o histórico de uma só vez.',
        highlights: [
          'Opções de 50, 100 ou 200 resultados por página.',
          'Navegação para as páginas anterior e seguinte.',
          'Contagem total e posição atual sempre visíveis.',
        ],
        icon: 'timeline',
      },
      {
        id: 'structured-assessment-reports',
        title: 'Relatórios clínicos mais completos',
        summary: 'Resultados estruturados para revisar, compartilhar e acompanhar o paciente.',
        description:
          'Os relatórios de avaliação reúnem os achados clínicos de forma organizada e ficam conectados aos demais pontos do cuidado.',
        highlights: [
          'Resumo das avaliações e relatório técnico com autoria protegida.',
          'Avaliações incluídas nos relatórios da agenda.',
          'Títulos dos relatórios identificados na timeline clínica.',
        ],
        icon: 'design',
      },
      {
        id: 'traceable-clinical-references',
        title: 'Referências clínicas rastreáveis',
        summary: 'Veja qual regra e versão sustentam cada interpretação do relatório.',
        description:
          'HADS e mMRC agora entram no relatório com suas referências; instrumentos sem critério classificatório seguro permanecem descritivos sem impedir o salvamento.',
        highlights: [
          'HADS com chave validada item a item e perfil brasileiro de rastreio.',
          'LCADL brasileira de 15 itens; registros antigos preservados como legados.',
          'Auditoria das regras selecionadas, versões, fontes e motivos de exclusão.',
          'Classificações sem fonte removidas de AQLQ, SGRQ, força respiratória e força periférica.',
        ],
        icon: 'assessment',
      },
    ],
  },
  {
    id: '2026-07-19-assessment-experience',
    versionLabel: 'Julho de 2026',
    publishedAt: '19/07/2026',
    title: 'Avaliações mais claras e consistentes',
    introduction:
      'Os testes clínicos ficaram mais simples de preencher, revisar, comparar e compartilhar com o paciente.',
    audience: ['admin', 'doctor', 'fisioterapeuta', 'interno', 'superAdmin'],
    steps: [
      {
        id: 'clearer-results',
        title: 'Resultados mais fáceis de interpretar',
        summary: 'Pré-visualizações mais limpas, com menos repetições e alertas mais claros.',
        description:
          'Testes funcionais, respiratórios e questionários agora seguem o mesmo padrão visual e destacam somente as informações clínicas importantes.',
        highlights: [
          'Cores e classificações padronizadas.',
          'Campos vazios deixam de ocupar espaço.',
          'Edição dos resultados disponível nos testes compatíveis.',
        ],
        icon: 'assessment',
      },
      {
        id: 'cleaner-pdfs',
        title: 'Laudos mais objetivos',
        summary: 'PDFs compactos, consistentes e pensados para o paciente.',
        description:
          'Os laudos priorizam o resultado, a interpretação e os dados realmente úteis, tentando manter tudo em uma página sempre que possível.',
        highlights: [
          'Cabeçalho, rodapé e paginação padronizados.',
          'Informações técnicas redundantes foram removidas.',
          'Quebras de página respeitam tabelas e assinaturas.',
        ],
        icon: 'design',
      },
      {
        id: 'history-and-shortcuts',
        title: 'Histórico e atalhos personalizados',
        summary: 'Compare resultados anteriores e encontre rapidamente os testes mais usados.',
        description:
          'As avaliações exibem comparações simples com resultados anteriores, enquanto a área “Mais usados” se adapta ao uso de cada clínica.',
        highlights: [
          'Diferença entre o resultado atual e o anterior.',
          'Histórico recente sem gráficos desnecessários.',
          'Atalhos calculados separadamente para cada tenant.',
        ],
        icon: 'timeline',
      },
    ],
  },
  {
    id: '2026-07-17-clinical-workflow',
    versionLabel: 'Julho de 2026',
    publishedAt: '17/07/2026',
    title: 'Um prontuário mais clínico e mais objetivo',
    introduction:
      'A evolução ficou mais limpa, enquanto o histórico relevante do paciente ganhou uma timeline própria.',
    audience: ['admin', 'doctor', 'fisioterapeuta', 'interno', 'superAdmin'],
    steps: [
      {
        id: 'new-evolution',
        title: 'Nova evolução mais objetiva',
        summary: 'Menos informação antes do campo principal e referências clínicas sob demanda.',
        description:
          'Paciente, data, agendamento e referências clínicas foram reorganizados para que o registro da evolução continue sendo a ação principal.',
        highlights: [
          'Agendamento opcional em formato compacto.',
          'Última evolução disponível para consulta e cópia.',
          'Atalhos clínicos reunidos na mesma linha.',
        ],
        icon: 'evolution',
      },
      {
        id: 'clinical-timeline',
        title: 'Timeline clínica do paciente',
        summary: 'Anamnese, eventos, exames e avaliações em ordem cronológica.',
        description:
          'A timeline concentra os marcos que ajudam a compreender a trajetória clínica sem misturá-los às evoluções de cada atendimento.',
        highlights: [
          'Eventos como exacerbação, hospitalização e alta.',
          'Edição dos eventos clínicos com atualização imediata.',
          'Alta hospitalar separada da alta do tratamento.',
        ],
        icon: 'timeline',
      },
      {
        id: 'exam-capture',
        title: 'Exames direto da câmera',
        summary: 'Fotografe várias páginas e salve tudo em um único documento.',
        description:
          'O novo atalho permite capturar páginas, ajustar sua ordem e gerar um PDF compacto no mesmo armazenamento já usado pelo prontuário.',
        highlights: [
          'Captura pela câmera ou seleção da galeria.',
          'Até 20 páginas por exame e limite de 10 MB.',
          'Disponível na timeline e também em Documentos.',
        ],
        icon: 'exam',
      },
      {
        id: 'vital-signs',
        title: 'Sinais vitais estruturados',
        summary: 'FC, SpO₂, FR, PA e Borg com preenchimento opcional.',
        description:
          'Os dados aferidos agora podem ser registrados separadamente do texto clínico e são apresentados de forma responsiva no prontuário.',
        highlights: [
          'Borg CR10 para dispneia e fadiga, inicial e final.',
          'Campos não preenchidos são totalmente omitidos.',
          'Sinais vitais de documentos assinados ficam protegidos.',
        ],
        icon: 'vitals',
      },
      {
        id: 'assessment-flow',
        title: 'Avaliações conectadas ao prontuário',
        summary: 'Inicie uma avaliação com o paciente já selecionado e retorne ao fluxo.',
        description:
          'Avaliações concluídas passam a compor automaticamente a timeline clínica, mesmo quando iniciadas fora da evolução.',
        highlights: [
          'Paciente pré-selecionado ao usar o atalho.',
          'Notificação da evolução quando a avaliação termina.',
          'Agrupamento dos resultados por sessão de avaliação.',
        ],
        icon: 'assessment',
      },
      {
        id: 'visual-language',
        title: 'Visual mais discreto e consistente',
        summary: 'Ações e estados com uma paleta mais profissional em todas as telas.',
        description:
          'Botões, alertas e áreas clínicas foram ajustados para reduzir ruído visual sem perder a identificação de sucesso, atenção ou erro.',
        highlights: [
          'Tons pastéis alinhados à identidade do RevaHub.',
          'Ações reorganizadas em telas menores.',
          'Melhor leitura de textos e campos longos.',
        ],
        icon: 'design',
      },
    ],
  },
];

export const normalizeWhatsNewAudience = (role?: string | null): WhatsNewAudience | null => {
  if (!role) return null;
  const normalizedRole = role === 'super_admin' ? 'superAdmin' : role;
  return WHATS_NEW_RELEASES.some((release) =>
    release.audience.includes(normalizedRole as WhatsNewAudience),
  )
    ? (normalizedRole as WhatsNewAudience)
    : null;
};

export const getWhatsNewReleasesForRole = (role?: string | null): WhatsNewRelease[] => {
  const audience = normalizeWhatsNewAudience(role);
  if (!audience) return [];
  return WHATS_NEW_RELEASES.filter((release) => release.audience.includes(audience));
};

export const hasWhatsNewForRole = (role?: string | null): boolean =>
  getWhatsNewReleasesForRole(role).length > 0;
